import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Users, 
  Globe, 
  Mail, 
  Phone, 
  TrendingUp,
  ArrowRight,
  Rocket,
  CheckCircle,
  XCircle,
  Ban,
  Clock,
  BarChart3,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ADMIN_BASE = "/admin";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email Safety Banner Component
const EmailSafetyBanner = ({ safetyStatus }) => {
  if (!safetyStatus) return null;
  
  const { safety_mode, use_mock_email, email_sending_enabled, allowlist_enabled, allowlist, blocked_emails_count } = safetyStatus;
  
  // Don't show banner if fully live
  if (safety_mode === "live") {
    return (
      <div className="safety-banner safety-banner-live">
        <ShieldCheck size={20} />
        <div>
          <strong>Live Email Sending Active</strong>
          <span>Campaign emails will be sent to real recipients.</span>
        </div>
      </div>
    );
  }
  
  if (use_mock_email) {
    return (
      <div className="safety-banner safety-banner-mock">
        <AlertTriangle size={20} />
        <div>
          <strong>Mock Email Mode</strong>
          <span>Emails are stored locally, not sent. Set USE_MOCK_EMAIL=false to enable SMTP.</span>
        </div>
      </div>
    );
  }
  
  if (!email_sending_enabled) {
    return (
      <div className="safety-banner safety-banner-disabled">
        <ShieldAlert size={20} />
        <div>
          <strong>Email Sending Disabled (Safety Guard)</strong>
          <span>Campaign emails are blocked. Set EMAIL_SENDING_ENABLED=true to allow sending.</span>
          {blocked_emails_count > 0 && (
            <span className="blocked-count">{blocked_emails_count} email(s) blocked</span>
          )}
        </div>
      </div>
    );
  }
  
  if (allowlist_enabled) {
    return (
      <div className="safety-banner safety-banner-allowlist">
        <ShieldAlert size={20} />
        <div>
          <strong>Allowlist Mode Active</strong>
          <span>Only sending to: {allowlist.join(', ')}</span>
          {blocked_emails_count > 0 && (
            <span className="blocked-count">{blocked_emails_count} email(s) blocked</span>
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

const MetricCard = ({ icon: Icon, label, value, subValue, color = "text-foreground" }) => (
  <div className="metric-card animate-fade-in" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="metric-value" style={{ color: `hsl(var(--${color === 'accent' ? 'accent' : 'foreground'}))` }}>
          {value}
        </p>
        <p className="metric-label">{label}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-1">{subValue}</p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${color === 'accent' ? 'bg-blue-50' : 'bg-muted'}`}>
        <Icon size={24} className={color === 'accent' ? 'text-blue-600' : 'text-muted-foreground'} />
      </div>
    </div>
  </div>
);

const statusColors = {
  running: "status-info",
  completed: "status-success",
  paused: "status-warning",
  failed: "status-error",
  draft: "status-badge bg-muted text-muted-foreground"
};

const FastActionBadge = ({ expiresAt }) => {
  if (!expiresAt) return null;
  
  const now = new Date();
  const expires = new Date(expiresAt);
  const isExpired = expires < now;
  const hoursLeft = Math.max(0, Math.floor((expires - now) / (1000 * 60 * 60)));
  
  if (isExpired) {
    return (
      <span className="text-xs text-red-500 flex items-center gap-1">
        <Clock size={12} />
        Expired
      </span>
    );
  }
  
  return (
    <span className="text-xs text-yellow-600 flex items-center gap-1">
      <Clock size={12} />
      {hoursLeft}h left
    </span>
  );
};

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [industryPerformance, setIndustryPerformance] = useState([]);
  const [industryDays, setIndustryDays] = useState("30");
  const [emailSafetyStatus, setEmailSafetyStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchIndustryPerformance(parseInt(industryDays));
  }, [industryDays]);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, campaignsRes, leadsRes, safetyRes] = await Promise.all([
        axios.get(`${API}/dashboard/metrics`),
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/leads?limit=5`),
        axios.get(`${API}/email-safety-status`).catch(() => ({ data: null }))
      ]);
      
      setMetrics(metricsRes.data);
      setCampaigns(campaignsRes.data.slice(0, 5));
      setRecentLeads(leadsRes.data);
      setEmailSafetyStatus(safetyRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustryPerformance = async (days) => {
    try {
      const res = await axios.get(`${API}/dashboard/industry-performance?days=${days}`);
      setIndustryPerformance(res.data);
    } catch (error) {
      console.error("Failed to fetch industry performance:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Email Safety Banner */}
      <EmailSafetyBanner safetyStatus={emailSafetyStatus} />
      
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your lead-to-website automation</p>
        </div>
        <Link to={`${ADMIN_BASE}/campaigns/new`}>
          <Button data-testid="start-campaign-btn" className="gap-2">
            <Rocket size={18} />
            Start Campaign
          </Button>
        </Link>
      </div>
      
      <div className="page-content">
        {/* Metrics Grid */}
        <div className="dashboard-grid">
          <MetricCard 
            icon={Users} 
            label="Total Leads" 
            value={metrics?.total_leads || 0}
            subValue={metrics?.leads_skipped > 0 ? `${metrics.leads_skipped} skipped` : null}
          />
          <MetricCard 
            icon={Globe} 
            label="Previews Generated" 
            value={metrics?.previews_generated || 0}
            color="accent"
          />
          <MetricCard 
            icon={Mail} 
            label="Emails Sent" 
            value={metrics?.emails_sent || 0}
            subValue={metrics?.email_to_call_rate > 0 ? `${metrics.email_to_call_rate}% → call` : null}
          />
          <MetricCard 
            icon={Phone} 
            label="Calls Booked" 
            value={metrics?.calls_booked || 0}
            subValue={metrics?.call_to_won_rate > 0 ? `${metrics.call_to_won_rate}% → won` : null}
          />
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-sm">Deals Won</span>
                  </div>
                  <span className="font-semibold">{metrics?.deals_won || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle size={16} className="text-red-500" />
                    <span className="text-sm">Deals Lost</span>
                  </div>
                  <span className="font-semibold">{metrics?.deals_lost || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ban size={16} className="text-yellow-500" />
                    <span className="text-sm">Leads Skipped</span>
                  </div>
                  <span className="font-semibold">{metrics?.leads_skipped || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Rocket size={16} className="text-blue-500" />
                    <span className="text-sm">Active Campaigns</span>
                  </div>
                  <span className="font-semibold">{metrics?.active_campaigns || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Campaigns */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold">Recent Campaigns</CardTitle>
              <Link to={`${ADMIN_BASE}/campaigns/new`} className="text-sm text-accent hover:underline flex items-center gap-1">
                View all <ArrowRight size={14} />
              </Link>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No campaigns yet. Start your first campaign!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign) => (
                    <div 
                      key={campaign.campaign_id} 
                      className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                      data-testid={`campaign-${campaign.campaign_id}`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{campaign.category} - {campaign.city}, {campaign.state}</p>
                          {campaign.campaign_type === "stock_load" ? (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                              S&L
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                              Web
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {campaign.leads_sourced} leads • {campaign.emails_sent} emails
                          {campaign.campaign_type !== "stock_load" && campaign.previews_generated > 0 && ` • ${campaign.previews_generated} previews`}
                          {campaign.leads_skipped > 0 && ` • ${campaign.leads_skipped} skipped`}
                        </p>
                      </div>
                      <Badge className={statusColors[campaign.status] || "status-badge"}>
                        {campaign.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Industry Performance */}
        <Card className="mb-8">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-accent" />
              <CardTitle className="text-base font-semibold">Industry Performance</CardTitle>
            </div>
            <Select value={industryDays} onValueChange={setIndustryDays}>
              <SelectTrigger className="w-[140px]" data-testid="industry-time-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="0">All time</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="p-0">
            {industryPerformance.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No industry data yet. Run campaigns to see performance by industry.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Industry</th>
                    <th>Leads</th>
                    <th>Emails</th>
                    <th>Calls</th>
                    <th>Won</th>
                    <th>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {industryPerformance.map((ind) => (
                    <tr key={ind.industry} data-testid={`industry-row-${ind.industry}`}>
                      <td>
                        <div>
                          <span className="font-medium">{ind.industry}</span>
                          <span className="text-xs text-muted-foreground ml-2">({ind.industry_group})</span>
                        </div>
                      </td>
                      <td className="tabular-nums">{ind.leads_sourced}</td>
                      <td className="tabular-nums">{ind.emails_sent}</td>
                      <td className="tabular-nums">{ind.calls_booked}</td>
                      <td className="tabular-nums font-semibold text-green-600">{ind.deals_won}</td>
                      <td className="tabular-nums">
                        {ind.email_to_won_rate > 0 ? (
                          <span className="text-green-600">{ind.email_to_won_rate}%</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Recent Leads */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Recent Leads</CardTitle>
            <Link to={`${ADMIN_BASE}/leads`} className="text-sm text-accent hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentLeads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No leads yet. Start a campaign to generate leads!</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Location</th>
                    <th>Industry</th>
                    <th>Status</th>
                    <th>Fast-Action</th>
                    <th>Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr key={lead.lead_id} className="table-row-hover" data-testid={`lead-row-${lead.lead_id}`}>
                      <td>
                        <Link to={`/admin/leads/${lead.lead_id}`} className="font-medium hover:text-accent">
                          {lead.business_name}
                        </Link>
                      </td>
                      <td className="text-muted-foreground">{lead.city}, {lead.state}</td>
                      <td>{lead.primary_industry || lead.category}</td>
                      <td>
                        <Badge className={
                          lead.status === 'email_sent' ? 'status-success' :
                          lead.status === 'preview_ready' ? 'status-info' :
                          lead.status === 'call_booked' ? 'status-warning' :
                          lead.status === 'won' ? 'status-success' :
                          lead.status === 'lost' ? 'status-error' :
                          'status-badge bg-muted text-muted-foreground'
                        }>
                          {lead.status.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td>
                        <FastActionBadge expiresAt={lead.fast_action_expires_at} />
                      </td>
                      <td>
                        {lead.preview_url ? (
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}${lead.preview_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:underline text-sm"
                          >
                            View Preview
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-sm">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
